import { buildEstimateEmailContent } from "./content.js";
import { RateLimiter } from "./rate-limiter.js";
import { isValidEmailAddress, normalizeEmailKey } from "./validation.js";
import type { EmailSendResult, EmailTransport, EstimateEmailRequest, EstimateEmailServiceConfig } from "./types.js";

const DEFAULT_RATE_LIMIT = { maxRequests: 3, windowMs: 10 * 60 * 1000 }; // 3 requests / 10 minutes per recipient
const DEFAULT_DUPLICATE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

export interface EstimateEmailService {
  sendEstimateEmail(request: EstimateEmailRequest): Promise<EmailSendResult>;
}

interface SentRecord {
  atMs: number;
  result: EmailSendResult;
}

/**
 * Reference implementation of the Email My Estimate feature (Phase 17).
 * Composes, in order: address validation, duplicate-submission
 * suppression, rate limiting, then handing a fully-built message to an
 * injected `EmailTransport`. Every branch returns a structured
 * `EmailSendResult` rather than throwing, so a caller (a REST handler, a
 * React form) never has to distinguish "this failed" from "this threw" -
 * the one exception is a request that fails basic shape validation before
 * any of that (see `sendEstimateEmail`'s guard clause), which is a
 * programmer error (a malformed `EstimateEmailRequest`), not a user-
 * facing outcome.
 *
 * State (rate-limit counters, the duplicate-suppression cache) is held
 * in-memory, same disclosed scope as `InMemoryLeadStore` - fine for a
 * single-process host or tests; a multi-instance deployment needs a
 * shared store (Redis, a database table) for these same two concerns.
 */
export class ReferenceEstimateEmailService implements EstimateEmailService {
  private readonly rateLimiter: RateLimiter;
  private readonly duplicateWindowMs: number;
  private readonly now: () => Date;
  private readonly sent = new Map<string, SentRecord>();

  constructor(
    private readonly config: EstimateEmailServiceConfig,
    private readonly transport: EmailTransport
  ) {
    this.rateLimiter = new RateLimiter(config.rateLimit ?? DEFAULT_RATE_LIMIT, () => this.now().getTime());
    this.duplicateWindowMs = config.duplicateWindowMs ?? DEFAULT_DUPLICATE_WINDOW_MS;
    this.now = config.now ?? (() => new Date());
  }

  async sendEstimateEmail(request: EstimateEmailRequest): Promise<EmailSendResult> {
    const requestId = request.requestId;

    if (!isValidEmailAddress(request.toEmail)) {
      return {
        status: "invalid_email",
        message: "That doesn't look like a valid email address - please double-check it and try again.",
        requestId
      };
    }

    const emailKey = normalizeEmailKey(request.toEmail);
    const dedupeKey = `${emailKey}::${request.idempotencyKey ?? requestId}`;

    const previouslySent = this.sent.get(dedupeKey);
    if (previouslySent && this.now().getTime() - previouslySent.atMs < this.duplicateWindowMs) {
      return {
        status: "duplicate_request",
        message: "We already emailed this estimate to that address a few minutes ago - check your inbox (and spam folder).",
        requestId
      };
    }

    if (!this.rateLimiter.tryConsume(emailKey)) {
      return {
        status: "rate_limited",
        message: "Too many estimate emails have been requested for this address recently - please try again in a few minutes.",
        requestId
      };
    }

    const { subject, text, html } = buildEstimateEmailContent(request.input, request.result, { siteUrl: this.config.siteUrl });

    try {
      await this.transport.send({
        to: request.toEmail,
        from: this.config.fromAddress,
        replyTo: this.config.replyToAddress,
        subject,
        text,
        html
      });
    } catch {
      // The underlying transport's error (a provider outage, an auth
      // failure, an invalid API key) is intentionally not surfaced here -
      // it's an internal-sending-infrastructure detail, not something the
      // end user submitting their email address needs or should see.
      return {
        status: "send_failed",
        message: "We couldn't send that email right now. Please try again shortly.",
        requestId
      };
    }

    const successResult: EmailSendResult = {
      status: "sent",
      message: "Your estimate has been emailed - check your inbox in the next few minutes.",
      requestId
    };
    this.sent.set(dedupeKey, { atMs: this.now().getTime(), result: successResult });
    return successResult;
  }
}

export function createEstimateEmailService(config: EstimateEmailServiceConfig, transport: EmailTransport): EstimateEmailService {
  return new ReferenceEstimateEmailService(config, transport);
}
